/**
 * The Genie — „Houdini vs. The Genie".
 *
 * INFINITE POWER: na začiatku ťahu smie odhodiť kartu za akciu navyše.
 * Three Wishes vie do konca ťahu zamknúť hodnoty jeho kariet na 4
 * (`turn.fixedValue`, uplatňuje ho `finishCombat` ako posledné).
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, drawCard, emit, gainAction, getMap, getPlayer, healFighter } from '../ops.js';
import { areEnemies } from '../players.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { feint } from './common.js';
import { opponentOf, revealHand } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const GENIE = 'the-genie';
const WISH = 'the-genie.your-wish-is-my-command';
const THREE = 'the-genie.three-wishes';
const WRATH = 'the-genie.imprisoned-wrath';
const FREED = 'the-genie.i-am-freed';

const genieOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === GENIE) ?? null;

/** Figúrky susediace s Géniom; `enemies` = len súperove. */
function aroundGenie(ctx: Ctx, player: PlayerIndex, enemies: boolean): FighterId[] {
  const genie = genieOf(ctx, player);
  if (!genie) return [];
  const around = new Set(neighbors(getMap(ctx), genie.spaceId));
  return ctx.state.fighters
    .filter((f) => around.has(f.spaceId) && (!enemies || areEnemies(ctx.state, f.owner, player)))
    .map((f) => f.id);
}

/** Hráč odhodí `count` kariet podľa vlastného výberu. */
function discardCards(ctx: Ctx, player: PlayerIndex, source: string | null, count: number, label: string): void {
  for (let i = 0; i < count; i++) {
    askChoice(ctx, {
      kind: 'card',
      player,
      source,
      label: `${label} (${i + 1} z ${count})`,
      optional: false,
      from: 'hand',
      purpose: 'discard',
      discards: true,
    });
  }
}

function damageChoice(e: EffectCtx | { ctx: Ctx; player: PlayerIndex; cardId: string }, targets: FighterId[], amount: number, label: string, optional: boolean): void {
  if (targets.length === 0) return;
  askChoice(e.ctx, { kind: 'damage', player: e.player, source: e.cardId, label, optional, amount, targets });
}

/** „If you won the combat, you may discard 2 cards to gain 1 action." */
const yourWish: CardEffect = {
  after: (e) => {
    if (!e.won || getPlayer(e.ctx.state, e.player).hand.length < 2 || e.ctx.state.turn.player !== e.player) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Your Wish Is My Command: odhodiť 2 karty za akciu navyše?',
      optional: true,
      effect: WISH,
      yes: 'Odhodiť 2 a získať akciu',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    discardCards(ctx, choice.player, choice.source, 2, 'Your Wish Is My Command: odhoď kartu');
    gainAction(ctx, choice.player);
  },
};

/** „Your opponent draws 1 card. Draw 3 cards." */
const wishingForMoreWishes: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent !== null) drawCard(e.ctx, opponent);
    for (let i = 0; i < 3; i++) drawCard(e.ctx, e.player);
  },
};

/**
 * „Gain 1 action and choose one effect: draw 5 cards / for the rest of your
 * turn, your cards' values are 4 and cannot be changed / each opponent
 * discards 2 cards."
 */
const threeWishes: CardEffect = {
  scheme: (e) => {
    gainAction(e.ctx, e.player);
    const follow = (data: string) => ({ kind: 'effect' as const, effect: THREE, data });
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Three Wishes: vyber jedno želanie',
      optional: false,
      options: [
        { label: 'Potiahni 5 kariet', follow: follow('draw') },
        { label: 'Do konca ťahu majú tvoje karty hodnotu 4', follow: follow('four') },
        { label: 'Každý súper odhodí 2 karty', follow: follow('discard') },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    const player = choice.player;
    if (data === 'draw') {
      for (let i = 0; i < 5; i++) drawCard(ctx, player);
    } else if (data === 'four') {
      ctx.state.turn.fixedValue = { player, value: 4 };
      emit(ctx, { type: 'specialUsed', player, heroId: GENIE, name: 'Three Wishes: hodnoty kariet sú do konca ťahu 4' });
    } else {
      ctx.state.players.forEach((seat, index) => {
        if (seat.eliminated || !areEnemies(ctx.state, index, player)) return;
        const count = Math.min(2, seat.hand.length);
        if (count > 0) discardCards(ctx, index, choice.source, count, 'Three Wishes: odhoď kartu');
      });
    }
  },
};

/** „DURING: The value of your opponent's card is equal to its BOOST value." */
const noParlorTrick: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (combat) (combat.flags ??= []).push(e.role === 'defender' ? 'boostValue:attacker' : 'boostValue:defender');
  },
};

/** „Draw cards equal to the amount of combat damage you took." */
const prisonersTorment: CardEffect = {
  after: (e) => {
    for (let i = 0; i < (e.damage ?? 0); i++) drawCard(e.ctx, e.player);
  },
};

/** „Look at your opponent's hand and choose a card for them to discard." */
const sultans: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || getPlayer(e.ctx.state, opponent).hand.length === 0) return;
    if (!revealHand(e, opponent)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: "I've Made Sultans Out of Less: vyber kartu, ktorú súper odhodí",
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'discard',
    });
  },
};

/** „You may discard 2 cards to deal 2 damage to an adjacent opposing fighter." */
const imprisonedWrath: CardEffect = {
  after: (e) => {
    if (getPlayer(e.ctx.state, e.player).hand.length < 2 || aroundGenie(e.ctx, e.player, true).length === 0) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Imprisoned Wrath: odhodiť 2 karty za 2 poškodenia susednému súperovi?',
      optional: true,
      effect: WRATH,
      yes: 'Odhodiť 2',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    discardCards(ctx, choice.player, choice.source, 2, 'Imprisoned Wrath: odhoď kartu');
    damageChoice({ ctx, player: choice.player, cardId: choice.source ?? '' }, aroundGenie(ctx, choice.player, true), 2, 'Imprisoned Wrath: 2 poškodenia susednému súperovi', false);
  },
};

/** „You may deal 1 damage to an adjacent fighter." */
const iGrantYouDeath: CardEffect = {
  after: (e) => damageChoice(e, aroundGenie(e.ctx, e.player, false), 1, 'I Grant You Death: smieš dať 1 poškodenie susednej figúrke', true),
};

/** „Place the Genie in any empty space. Then, deal 1 damage to each adjacent fighter." */
const iAmFreed: CardEffect = {
  after: (e) => {
    const genie = genieOf(e.ctx, e.player);
    if (!genie) return;
    const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'I Am Freed: polož Génia na ľubovoľný prázdny priestor',
      optional: false,
      fighterId: genie.id,
      spaces: getMap(e.ctx).spaces.map((s) => s.id).filter((id) => !occupied.has(id)),
    });
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'I Am Freed: poškodenie susedom',
      optional: false,
      follow: { kind: 'effect', effect: FREED, data: '' },
    });
  },
  onFollow: (ctx, choice) => {
    for (const id of aroundGenie(ctx, choice.player, false)) dealDamage(ctx, id, 1);
  },
};

/** „IMMEDIATELY: The Genie recovers 4 health." */
const backInTheLamp: CardEffect = {
  immediate: (e) => {
    const genie = genieOf(e.ctx, e.player);
    if (genie) healFighter(e.ctx, genie.id, 4);
  },
};

/** „If you lost the combat, deal 1 damage to an adjacent opposing fighter." */
const carefulWhatYouWishFor: CardEffect = {
  after: (e) => {
    if (!e.won) damageChoice(e, aroundGenie(e.ctx, e.player, true), 1, 'Careful What You Wish For: 1 poškodenie susednému súperovi', false);
  },
};

export const genieCards: EffectRegistry = {
  [WISH]: yourWish,
  'the-genie.wishing-for-more-wishes': wishingForMoreWishes,
  [THREE]: threeWishes,
  'the-genie.this-is-no-parlor-trick': noParlorTrick,
  'the-genie.prisoners-torment': prisonersTorment,
  'the-genie.ive-made-sultans-out-of-less': sultans,
  [WRATH]: imprisonedWrath,
  'the-genie.i-grant-you-death': iGrantYouDeath,
  [FREED]: iAmFreed,
  'the-genie.back-in-the-lamp': backInTheLamp,
  'the-genie.careful-what-you-wish-for': carefulWhatYouWishFor,
  'the-genie.feint': feint,
};

export const genieSpecial: HeroSpecial = {
  name: 'Infinite Power',
  /** „At the start of your turn, you may discard 1 card to gain 1 action." */
  startOfTurn: (ctx, player) => {
    if (getPlayer(ctx.state, player).hand.length === 0) return;
    askChoice(ctx, {
      kind: 'confirm',
      player,
      source: null,
      label: 'Infinite Power: odhodiť 1 kartu za akciu navyše?',
      optional: true,
      special: GENIE,
      yes: 'Odhodiť a získať akciu',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, player) => {
    discardCards(ctx, player, null, 1, 'Infinite Power: odhoď kartu');
    gainAction(ctx, player);
  },
};

